import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Header } from '../shared/header/header';
import { SupplierFilter } from '../shared/components/supplier-filter/supplier-filter';
import { PaymentService } from './payment.service';
import { PaymentState } from './payment.state';
import { SettlementProofDialog, SettlementProofDialogResult } from './settlement-proof-dialog/settlement-proof-dialog';

/**
 * Manual settlement of what suppliers are owed.
 *
 * Nothing here moves money. An admin
 * selects the unpaid runs and/or manual contracts they have just paid for, and
 * the settlement records what was covered, with optional proof images. The
 * amount is summed server-side from those rows, so it can never disagree with
 * what it claims to pay.
 *
 * This is the shell: shared filters, refresh, the tab navigation (each tab is
 * its own sub-route so a reload stays on it) and the settlement bar. The
 * tables live in the routed children.
 */
interface TabView {
  path: string;
  label: string;
  icon: string;
}

interface TabGroup {
  label: string;
  /** Mobile label. */
  short: string;
  icon: string;
  views: TabView[];
}

@Component({
  selector: 'app-payment',
  imports: [
    CommonModule, FormsModule, RouterOutlet, RouterLink, RouterLinkActive,
    MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, MatTabsModule, MatTooltipModule,
    Header, SupplierFilter,
  ],
  providers: [PaymentState],
  templateUrl: './payment.html',
  styleUrl: './payment.scss',
})
export class Payment implements OnInit {
  readonly state = inject(PaymentState);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private paymentService = inject(PaymentService);

  /**
   * Six screens grouped by job so the tab bar stays short: what is owed, what
   * suppliers asked for, what was paid, and the payout setup. A group with more
   * than one view gets a small switch under the tab bar. URLs are unchanged.
   */
  readonly groups: TabGroup[] = [
    {
      label: 'To pay', short: 'To pay', icon: 'pending_actions',
      views: [
        { path: 'runs', label: 'Transfer runs', icon: 'swap_horiz' },
        { path: 'contracts', label: 'Manual contracts', icon: 'description' },
      ],
    },
    { label: 'Payment requests', short: 'Requests', icon: 'request_quote', views: [{ path: 'requests', label: 'Payment requests', icon: 'request_quote' }] },
    { label: 'Settlements', short: 'Settled', icon: 'receipt_long', views: [{ path: 'settlements', label: 'Settlements', icon: 'receipt_long' }] },
    {
      label: 'Setup', short: 'Setup', icon: 'settings',
      views: [
        { path: 'methods', label: 'Payout methods', icon: 'account_balance_wallet' },
        { path: 'sources', label: 'Paid-from accounts', icon: 'account_balance' },
        { path: 'crypto', label: 'Crypto payouts', icon: 'currency_bitcoin' },
      ],
    },
  ];

  /** First path segment under /payments, e.g. 'runs' or 'requests' (also for requests/:id). */
  private get currentPath(): string {
    const path = this.router.url.split('?')[0];
    return path.split('/payments/')[1]?.split('/')[0] ?? '';
  }

  get activeGroup(): TabGroup | undefined {
    const current = this.currentPath;
    return this.groups.find(g => g.views.some(v => v.path === current));
  }

  ngOnInit(): void {
    // Filters ride in the URL so a reload keeps them, like it keeps the tab.
    const q = this.route.snapshot.queryParamMap;
    this.state.supplierId = q.get('supplier') ?? '';
    this.state.fromDate = q.get('from') ?? '';
    this.state.toDate = q.get('to') ?? '';
  }

  /**
   * The shared supplier/date filters do not apply on a request's own page
   * (already scoped to its supplier) or the setup tabs (payout methods,
   * paid-from accounts, crypto payouts).
   */
  get hideFilters(): boolean {
    const isRequestDetail = this.currentPath === 'requests' && /\/payments\/requests\/[^/?]+/.test(this.router.url);
    return isRequestDetail || ['methods', 'sources', 'crypto'].includes(this.currentPath);
  }

  /** The requests list has its own date-and-time range (with pay-via and amount) in its toolbar. */
  get hideDates(): boolean {
    return this.currentPath === 'requests';
  }

  get multipleSuppliers(): boolean {
    return this.state.selectedSupplierIds.length > 1;
  }

  onSupplierChange(id: string): void {
    this.state.supplierId = id;
    this.filtersChanged();
  }

  filtersChanged(): void {
    this.router.navigate([], {
      queryParams: {
        supplier: this.state.supplierId || null,
        from: this.state.fromDate || null,
        to: this.state.toDate || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.state.reload();
  }

  clearFilters(): void {
    this.state.supplierId = '';
    this.state.fromDate = '';
    this.state.toDate = '';
    this.filtersChanged();
  }

  refresh(): void {
    this.state.reload();
  }

  settle(): void {
    const supplierIds = this.state.selectedSupplierIds;
    if (supplierIds.length === 0) return;
    if (supplierIds.length > 1) {
      this.snackBar.open(
        'Select items for a single supplier: one settlement covers one supplier.',
        'Close', { duration: 6000 });
      return;
    }

    const runIds = [...this.state.runs.keys()];
    const contractIds = [...this.state.contracts.keys()];
    const amount = this.state.selectedTotal;
    // The backend refuses it too; this just says why before the dialog opens.
    if (amount <= 0) {
      this.snackBar.open('The selected items add up to nothing to pay.', 'Close', { duration: 6000 });
      return;
    }
    const parts = [
      runIds.length ? `${runIds.length} run${runIds.length === 1 ? '' : 's'}` : '',
      contractIds.length ? `${contractIds.length} contract${contractIds.length === 1 ? '' : 's'}` : '',
    ].filter(Boolean);

    const ref = this.dialog.open(SettlementProofDialog, {
      width: '440px',
      maxWidth: '95vw',
      data: {
        title: 'Record settlement',
        submitLabel: 'Record settlement',
        summary: parts.join(' + '),
        amount,
      },
    });
    ref.afterClosed().subscribe((result: SettlementProofDialogResult | null) => {
      if (!result) return;
      this.paymentService.createSettlement({
        supplier_id: supplierIds[0],
        run_ids: runIds,
        contract_ids: contractIds,
        note: result.note || undefined,
      }, result.proofFiles).subscribe({
        next: s => {
          this.snackBar.open(
            `Settlement recorded for ${s.amount.toLocaleString()} — mark it paid once the transfer is sent.`,
            'Close', { duration: 6000 });
          this.state.reload();
        },
        error: err => this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 6000 }),
      });
    });
  }
}
